import { apiClient } from './client';
import {
  Report,
  ReportFormData,
  ReportSection,
  ReportItem,
  ApiResponse,
  PaginatedResponse,
} from '@/types';

export const reportsApi = {
  // Get all reports for current user
  getReports: async (page = 1, pageSize = 10) => {
    return apiClient.get<PaginatedResponse<Report>>(
      `/reports?page=${page}&pageSize=${pageSize}`
    );
  },

  // Get single report by ID
  getReport: async (id: string) => {
    return apiClient.get<ApiResponse<Report>>(`/reports/${id}`);
  },

  // Create new report
  createReport: async (data: ReportFormData) => {
    return apiClient.post<ApiResponse<Report>>('/reports', data);
  },

  // Update report
  updateReport: async (id: string, data: Partial<Report>) => {
    return apiClient.put<ApiResponse<Report>>(`/reports/${id}`, data);
  },

  // Save inspector-edited AI summary text (backend field is snake_case)
  updateAiSummary: async (id: string, aiSummary: string) => {
    return apiClient.put<ApiResponse<Report>>(`/reports/${id}`, { ai_summary: aiSummary });
  },

  // Delete report
  deleteReport: async (id: string) => {
    return apiClient.delete<ApiResponse<void>>(`/reports/${id}`);
  },

  // Add section to report
  addSection: async (reportId: string, section: Omit<ReportSection, 'id' | 'reportId'>) => {
    return apiClient.post<ApiResponse<ReportSection>>(
      `/reports/${reportId}/sections`,
      section
    );
  },

  // Update section
  updateSection: async (reportId: string, sectionId: string, data: Partial<ReportSection>) => {
    return apiClient.put<ApiResponse<ReportSection>>(
      `/reports/${reportId}/sections/${sectionId}`,
      data
    );
  },

  // Delete section
  deleteSection: async (reportId: string, sectionId: string) => {
    return apiClient.delete<ApiResponse<void>>(
      `/reports/${reportId}/sections/${sectionId}`
    );
  },

  // Reorder sections
  reorderSections: async (reportId: string, sectionIds: string[]) => {
    return apiClient.post<ApiResponse<void>>(
      `/reports/${reportId}/sections/reorder`,
      { sectionIds }
    );
  },

  // Add item to section
  addItem: async (reportId: string, sectionId: string, item: Omit<ReportItem, 'id' | 'sectionId'>) => {
    return apiClient.post<ApiResponse<ReportItem>>(
      `/reports/${reportId}/sections/${sectionId}/items`,
      item
    );
  },

  // Update item
  updateItem: async (
    reportId: string,
    sectionId: string,
    itemId: string,
    data: Partial<ReportItem>
  ) => {
    return apiClient.put<ApiResponse<ReportItem>>(
      `/reports/${reportId}/sections/${sectionId}/items/${itemId}`,
      data
    );
  },

  // Delete item
  deleteItem: async (reportId: string, sectionId: string, itemId: string) => {
    return apiClient.delete<ApiResponse<void>>(
      `/reports/${reportId}/sections/${sectionId}/items/${itemId}`
    );
  },

  // Reorder items in section
  reorderItems: async (reportId: string, sectionId: string, itemIds: string[]) => {
    return apiClient.post<ApiResponse<void>>(
      `/reports/${reportId}/sections/${sectionId}/items/reorder`,
      { itemIds }
    );
  },

  // Generate AI summary (longer timeout — Claude can take up to 60s)
  generateAISummary: async (reportId: string) => {
    return apiClient.post<ApiResponse<{ summary: string }>>(
      `/reports/${reportId}/ai-summary`,
      undefined,
      { timeout: 90000 }
    );
  },

  // Generate PDF (returns base64 for in-tab preview; Safari-safe since caller opens tab synchronously)
  generatePDF: async (reportId: string) => {
    return apiClient.post<ApiResponse<{ pdfUrl: string; pdfBase64: string }>>(
      `/reports/${reportId}/generate-pdf`
    );
  },

  // Send report to client (and optionally to the buyer's agent). Agent
  // fields and feeAmount are persisted on the report so subsequent sends
  // remember them. feeAmount is omitted from the body to leave the stored
  // value untouched; pass it explicitly (including 0) to overwrite.
  sendReport: async (
    reportId: string,
    args: {
      recipientEmail?: string;
      agentName?: string;
      agentEmail?: string;
      feeAmount?: number;
    } = {}
  ) => {
    return apiClient.post<
      ApiResponse<{
        portalUrl: string;
        emailSent: boolean;
        clientEmailSent: boolean;
        clientEmailError: string | null;
        agentEmailSent: boolean | null;
        agentEmailError: string | null;
        findingCounts: {
          safetyConcerns: number;
          recommendations: number;
          totalItems: number;
        };
      }>
    >(`/reports/${reportId}/send`, args);
  },

  // Get public report (no auth required)
  getPublicReport: async (publicId: string) => {
    return apiClient.get<ApiResponse<Report>>(`/reports/public/${publicId}`);
  },

  // Generate PDF for public report (no auth required)
  generatePublicPDF: async (publicId: string) => {
    return apiClient.post<ApiResponse<{ pdfBase64: string }>>(
      `/reports/public/${publicId}/generate-pdf`
    );
  },

  // Record a public view. Backend dedupes by ip within 1h.
  trackPublicView: async (publicId: string) => {
    return apiClient.post<ApiResponse<{ viewsCount: number; lastViewedAt: string }>>(
      `/reports/public/${publicId}/view`
    );
  },

  // Mark payment received. Idempotent — safe to call multiple times.
  // Optional paidAt lets the inspector record a past payment date.
  markPaid: async (reportId: string, paidAt?: string) => {
    return apiClient.post<ApiResponse<{ id: string; paymentReceivedAt: string }>>(
      `/reports/${reportId}/mark-paid`,
      paidAt !== undefined ? { paidAt } : {}
    );
  },
};
